import { useEffect, useState } from 'react';

/**
 * Hook de contagem regressiva baseado em um alvo absoluto (ISO 8601).
 *
 * Recalcula `secondsRemaining` a partir de `Date.now()` a cada segundo, até
 * chegar a 0 e parar - assim o valor reflete sempre a diferença real entre
 * "agora" e o alvo (e não um contador local que poderia dessincronizar do
 * servidor), e é restaurado corretamente mesmo se o componente for montado
 * no meio de uma janela de cooldown (ex.: reload de página).
 *
 * Uso:
 *   const secondsRemaining = useCountdown(user.cellphone_verification_limit?.cooldown_until);
 *
 * @param {string|null} targetIso - data/hora alvo em ISO 8601, ou null/undefined
 *   para "sem contagem" (retorna 0).
 * @returns {number} segundos restantes até o alvo (nunca negativo).
 */
export function useCountdown(targetIso) {
  const computeRemaining = () => {
    if (!targetIso) {
      return 0;
    }

    const diffMs = new Date(targetIso).getTime() - Date.now();

    return diffMs > 0 ? Math.ceil(diffMs / 1000) : 0;
  };

  const [secondsRemaining, setSecondsRemaining] = useState(computeRemaining);

  useEffect(() => {
    setSecondsRemaining(computeRemaining());

    if (!targetIso) {
      return undefined;
    }

    const intervalId = setInterval(() => {
      setSecondsRemaining(computeRemaining());
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIso]);

  return secondsRemaining;
}
